import React, { useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { Alert, Button, Flex, Heading, Text, TextAreaFormField } from '@indeed/ifl-components';
import { ArrowLeft, Close } from '@indeed/ifl-icons';
import useGettext from '../../../util/GettextFactory';
import { useInitialState } from '../../../lib/context/InitialStateContext';

import {
    log as logAutoApplySetupFeedbackEvent,
    AutoApplySetupFeedbackCategory,
    AvroOptional,
} from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupFeedbackEvent';

const isValidCategory = (value: string | undefined): value is AutoApplySetupFeedbackCategory =>
    value !== undefined && value in AutoApplySetupFeedbackCategory;

const MAX_CHARACTERS = 300;

interface MidSetupFeedbackDetailsStepProps {
    onClose: () => void;
    initialText?: string;
}

const MidSetupFeedbackDetailsStep: React.FC<MidSetupFeedbackDetailsStepProps> = ({
    onClose,
    initialText = '',
}) => {
    const { category } = useParams<{ category: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [additionalDetails, setAdditionalDetails] = useState<string>(initialText);
    const gt = useGettext();
    const { setupLogUid } = useInitialState();

    if (!isValidCategory(category)) {
        return (
            <Navigate
                to={{
                    pathname: '/auto-apply-setup/mid-setup-feedback',
                    search: searchParams.toString(),
                }}
                replace
            />
        );
    }

    const characterCount = additionalDetails.length;
    const isOverLimit = characterCount > MAX_CHARACTERS;

    const handleBack = () => {
        void navigate(-1);
    };

    const handleSubmit = () => {
        if (isOverLimit) return;

        const currentPageParam = searchParams.get('currentPage') || '';
        const currentPagePathname = currentPageParam.split('?')[0];

        logAutoApplySetupFeedbackEvent({
            setupLogUid: setupLogUid || '',
            currentPage: currentPagePathname,
            category: AutoApplySetupFeedbackCategory[category],
            content: AvroOptional.string(additionalDetails.trim() || null),
            country: AvroOptional.string(null),
            language: AvroOptional.string(null),
        });

        onClose();
    };

    return (
        <PageTransition pageKey="mid-setup-feedback-details">
            <Flex
                sx={{
                    flexDirection: 'column',
                }}
            >
                <Flex sx={{ justifyContent: 'space-between' }}>
                    <Button
                        variant="tertiary"
                        iconOnly
                        size="sm"
                        onClick={handleBack}
                        aria-label={gt.gettext('Back')}
                    >
                        <ArrowLeft aria-hidden="true" />
                    </Button>
                    <Button
                        variant="tertiary"
                        iconOnly
                        size="sm"
                        onClick={onClose}
                        aria-label={gt.gettext('Close')}
                    >
                        <Close aria-hidden="true" />
                    </Button>
                </Flex>

                <Flex
                    sx={{
                        flexDirection: 'column',
                        gap: 4,
                    }}
                >
                    <Flex sx={{ flexDirection: 'column' }}>
                        <Heading level={4} sx={{ marginBlockEnd: 1 }}>
                            {gt.gettext('Please tell us what happened')}
                        </Heading>
                        <Text level={2} variant={'secondary'}>
                            {gt.gettext("Your feedback won't be shared with the employer.")}
                        </Text>
                    </Flex>

                    <Alert>
                        {gt.gettext(
                            'Do not include any sensitive information such as phone numbers or email addresses.',
                        )}
                    </Alert>

                    <Flex sx={{ flexDirection: 'column', gap: 1 }}>
                        <TextAreaFormField
                            textareaProps={{
                                value: additionalDetails,
                                onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                    setAdditionalDetails(e.target.value);
                                },
                                rows: 5,
                                'aria-label': gt.gettext('Details'),
                            }}
                        />
                        <Text level={3} variant={isOverLimit ? 'alert' : 'secondary'}>
                            {characterCount}/{MAX_CHARACTERS}
                        </Text>
                    </Flex>

                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSubmit}
                        disabled={isOverLimit}
                    >
                        {gt.gettext('Submit feedback')}
                    </Button>
                </Flex>
            </Flex>
        </PageTransition>
    );
};

export default MidSetupFeedbackDetailsStep;
